import { Box, Checkbox, CircularProgress, Icon, InputAdornment, Typography } from '@mui/material'
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import ExternalTalentTable from '../components/table/ExternalTalentTable'
import DeleteTalentModal from '../components/DeleteTalentModal'
import ViewTalentDrawer from '../components/ViewTalentDrawer'
import { useMutation, useQueryClient } from 'react-query'
import {
  getRedundantTalent,
  keepTalent,
  redundantResume,
  removeTalent,
  searchCandidate,
} from 'services/api/modules/recruitment/external-talent'
import AdxInput from 'components/custom/AdxInput'
import { useDebouncedEffect } from 'services/hooks/useDebouncedEffect'

const EmailSpecificView = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  const [selectedIds, setSelectedIds] = useState([])
  const [talentId, setTalentId] = useState(null)

  //search & pagination
  const [search, setSearch] = useState('')
  const [searchItem, setSearchItem] = useState('')

  const handleClearSearch = () => {
    setSearchItem('')
  }

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const { data, isLoading } = useQuery(
    'getRedundant',
    async () => {
      const { data } = await redundantResume()
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const redundantEmail = location.state.redundantEmail
  const redundantData = data?.redundant_candidateable?.find((i) => i.email === redundantEmail)
  const redundantCount = data?.redundant_count

  const { mutate: removeCandidate } = useMutation(removeTalent, {
    onSuccess: () => {
      queryClient.invalidateQueries('getRedundant')
      setSelectedIds([])
      if (redundantData?.total - selectedIds.length <= 1 || redundantCount === 1) {
        navigate('/app/recruitment/external-talent' + (redundantCount === 1 ? '' : '/redundant'))
      }
    },
    onError: (error) => {
      console.error(error)
    },
  })

  const { mutate: keepCandidate } = useMutation(keepTalent, {
    onSuccess: () => {
      queryClient.invalidateQueries('getRedundant')
      navigate('/app/recruitment/external-talent' + (redundantCount === 1 ? '' : '/redundant'))
    },
    onError: (error) => {
      console.error(error)
    },
  })

  const handleCheckboxChange = (id) => (event) => {
    if (event.target.checked) {
      setSelectedIds((prevSelectedIds) => [...prevSelectedIds, id])
    } else {
      setSelectedIds((prevSelectedIds) => prevSelectedIds.filter((selectedId) => selectedId !== id))
    }
  }

  const handleRemove = (ids) => {
    const data = {
      candidate_id: ids,
    }

    removeCandidate(data)
  }

  const {
    isLoading: specificTalentIsLoading,
    error: specificTalentError,
    data: specificTalentData,
    isSuccess: specificTalentIsSuccess,
  } = useQuery(
    ['getRedundant', redundantEmail, page, rowsPerPage, search],
    async () => {
      const { data } = await getRedundantTalent(redundantEmail, page + 1, rowsPerPage, search)
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  //   set delay for search api
  useDebouncedEffect(
    () => {
      if (searchItem.length > 0) {
        setSearch(`&search=${searchItem}`)
      } else {
        setSearch('')
      }
    },
    [searchItem],
    700
  )

  const redundantColumn = [
    {
      id: 1,
      Header: 'Talent',
      accessor: 'name',
      Cell: (props) => {
        const { value } = props
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <Checkbox
              color="alternate"
              sx={{ color: 'light-gray-text.main' }}
              checked={selectedIds.includes(props?.row?.original?.id)}
              onChange={handleCheckboxChange(props?.row?.original?.id)}
              disabled={!props?.row?.original?.is_deleteable}
            />
            <Box
              sx={{
                width: '35px',
                height: '35px',
                backgroundColor: 'default-background.main',
                borderRadius: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Icon sx={{ fontSize: '20px', color: 'alternate.main' }}>
                person_outline_outlined
              </Icon>
            </Box>
            <Box>
              <Typography sx={{ marginBottom: '4px' }}>{value}</Typography>
              {/* <Typography variant='subtitle2' sx={{ color: 'light-gray-text.main' }}>{original.updated_date}</Typography> */}
            </Box>
          </Box>
        )
      },
    },
    {
      id: 2,
      Header: 'Email',
      accessor: 'email',
      Cell: (props) => {
        const { value } = props

        return <Typography sx={{ color: 'light-gray-text.main' }}>{value}</Typography>
      },
    },
    // {
    //   id: 3,
    //   Header: 'Profile score',
    //   accessor: 'profile_score',
    //   Cell: (props) => {
    //     const { value } = props

    //     return (
    //       <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
    //         <Icon sx={{ color: '#F3BF3A', fontSize: '16px' }}>stars_rounded</Icon>
    //         <Typography>{value}</Typography>
    //       </Box>
    //     )
    //   },
    // },
    // {
    //     id: 4,
    //     Header: 'Id',
    //     accessor: 'id',
    //     Cell: (props) => {
    //         const { value} = props
    //         return ( <Typography>{value}</Typography> )
    //     }
    // },
  ]

  return (
    <DashboardContainer>
      <Box sx={{ paddingBottom: '35px' }}>
        <Box
          sx={{
            color: 'light-gray-text.main',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '14px 14px 14px 0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Typography
              onClick={() => navigate('/app/recruitment/external-talent')}
              sx={{ color: 'alternate.main', cursor: 'pointer' }}
            >
              External talents
            </Typography>
            <Icon sx={{ color: 'light-gray-text-2.main', fontSize: '20px' }}>chevron_right</Icon>
            <Typography
              onClick={() => navigate('/app/recruitment/external-talent/redundant')}
              sx={{
                color: 'alternate.main',
                cursor: 'pointer',
              }}
            >
              Redundant talents{redundantCount ? ` (${redundantCount})` : ''}
            </Typography>
            <Icon sx={{ color: 'light-gray-text-2.main', fontSize: '20px' }}>chevron_right</Icon>
            <Typography>"{redundantEmail}"</Typography>
          </Box>
        </Box>
        <Box
          sx={{
            padding: '12px 16px',
            marginBottom: '16px',
            borderRadius: '2px',
            borderLeft: '3px solid #F3BF3A',
            backgroundColor: 'light-warning.main',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icon sx={{ color: 'warning.main' }}>error</Icon>
            <Typography>Keep one talent per email to resolve redundancy</Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '16px',
            backgroundColor: 'white.main',
            borderBottom: '1px solid #E5E5E5',
            borderRadius: '4.5px 4.5px 0 0',
          }}
        >
          <AdxInput
            placeholder="Search name"
            value={searchItem}
            onChange={(e) => setSearchItem(e.target.value)}
            sx={{ width: '300px' }}
            startAdornment={
              <InputAdornment position="start">
                <Icon>search</Icon>
              </InputAdornment>
            }
            endAdornment={
              searchItem ? (
                <InputAdornment position="end">
                  <Icon onClick={handleClearSearch} sx={{ color: 'error.main', cursor: 'pointer' }}>
                    close
                  </Icon>
                </InputAdornment>
              ) : null
            }
          />
          <DeleteTalentModal selectedIds={selectedIds} handleRemove={handleRemove} multipleDelete />
        </Box>
        {specificTalentIsLoading ? (
          <Box
            sx={{
              height: '50vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress color="alternate" />
          </Box>
        ) : (
          <>
            <ExternalTalentTable
              column={redundantColumn}
              data={specificTalentData?.data}
              nav={
                <ViewTalentDrawer
                  talentId={talentId}
                  handleRemove={handleRemove}
                  handleKeep={() => keepCandidate(talentId)}
                  isKeepable
                />
              }
              onClick={(e) => setTalentId(e.id)}
              count={redundantData?.total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              pagination
            />
          </>
        )}
      </Box>
    </DashboardContainer>
  )
}

export default EmailSpecificView
